import { defaultDarkTheme } from "react-admin";
import { createTheme, alpha } from "@mui/material/styles";

// ── Design tokens — mirrored from app/src/index.css (viewer = source of truth)
// --color-accent:        #D95441  (terracotta red)
// --color-accent-hover:  #E26A59
// --color-bg-warm:       #120d0d  (near-black, warm)
// --color-panel-warm:    rgba(20,15,15,0.5)
// Text grays: Tailwind zinc — zinc-200 #e4e4e7, zinc-400 #a1a1aa, zinc-500 #71717a
//   (the viewer uses text-zinc-* utility classes; keep these hex values in sync)
// Neutral hairlines: white at 10% alpha (viewer's border-white/10)
// Inputs: monospace, 6px radius, black/40 bg, accent/30 border (viewer's .popup-input)
// Font: "Bodoni Moda" serif (same Google Fonts URL as app/ — no opsz axis)
// Accepted difference: the viewer's panels are translucent glass (rgba bg +
// backdrop blur over the 3D scene); the admin keeps solid paper surfaces since
// there is no scene behind them to blur.

const accent = {
  50:  "#fdf0ee",
  100: "#f9d5cf",
  200: "#f3a89e",
  300: "#ec7b6e",
  // main accent — matches --color-accent exactly
  400: "#D95441",
  // hover accent — matches --color-accent-hover
  500: "#E26A59",
  600: "#c24030",
  700: "#9e3125",
  800: "#7a231a",
  900: "#4f1410",
};

// Warm dark backgrounds matching --color-bg-warm / --color-panel-warm
const warmBg = {
  // app background: #120d0d
  default: "#120d0d",
  // sidebar / app-bar: slightly lighter warm dark
  surface: "#180f0f",
  // card / paper: warm near-black with slight lift
  paper: "#1e1212",
  // deeper surface for inputs
  input: "#0e0a0a",
};

// Text grays — Tailwind zinc, matching the viewer's text-zinc-* classes
const text = {
  primary:   "#e4e4e7", // zinc-200 — value text
  secondary: "#a1a1aa", // zinc-400 — descriptions
  disabled:  "#71717a", // zinc-500 — micro-labels / muted
};

// Viewer hairline borders: border-white/10
const hairline = alpha("#ffffff", 0.1);

// Tailwind's font-mono stack — matches the viewer's input fields
const fontMono =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export const aereTheme = createTheme({
  ...defaultDarkTheme,
  palette: {
    ...defaultDarkTheme.palette,
    mode: "dark",
    primary: {
      light:        accent[300],
      main:         accent[400],   // #D95441 — terracotta accent
      dark:         accent[600],
      contrastText: "#ffffff",
    },
    secondary: {
      light:        accent[200],
      main:         accent[500],   // #E26A59 — hover accent
      dark:         accent[700],
      contrastText: "#ffffff",
    },
    error: {
      main: "#ff5252",
    },
    background: {
      default: warmBg.default,
      paper:   warmBg.paper,
    },
    text: {
      // Zinc grays to match the viewer's text-zinc-200/400/500 classes
      primary:   text.primary,
      secondary: text.secondary,
      disabled:  text.disabled,
    },
    divider: alpha(accent[400], 0.18),
  },

  // ── Typography — Bodoni Moda to match the viewer ──────────────────────────
  typography: {
    ...defaultDarkTheme.typography,
    fontFamily: '"Bodoni Moda", "Georgia", serif',
    // App-bar / page title
    h6: {
      fontWeight: 400,
      letterSpacing: "0.08em",
    },
    // Section headings
    h5: {
      fontWeight: 300,
      letterSpacing: "0.04em",
    },
    // Body text — keep readable at small sizes
    body1: {
      fontFamily: '"Bodoni Moda", "Georgia", serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Bodoni Moda", "Georgia", serif',
      fontWeight: 400,
    },
    // Uppercase labels / captions — tighten tracking like the app's tagline
    caption: {
      fontFamily: '"Bodoni Moda", "Georgia", serif',
      fontSize: "0.7rem",
      letterSpacing: "0.12em",
    },
    button: {
      fontFamily: '"Bodoni Moda", "Georgia", serif',
      fontWeight: 500,
      letterSpacing: "0.08em",
    },
  },

  // ── Shape — generous rounding to match sidebar-panel (rounded-3xl = 24px) ─
  shape: {
    borderRadius: 12,
  },

  components: {
    ...defaultDarkTheme.components,

    // ── Global baseline ───────────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: warmBg.default,
          fontFamily: '"Bodoni Moda", "Georgia", serif',
        },
        // Subtle warm scrollbar matching the app's hidden/minimal style
        "*::-webkit-scrollbar": {
          width: "5px",
          height: "5px",
        },
        "*::-webkit-scrollbar-track": {
          background: warmBg.surface,
        },
        "*::-webkit-scrollbar-thumb": {
          background: alpha(accent[400], 0.5),
          borderRadius: "4px",
          "&:hover": {
            background: alpha(accent[400], 0.75),
          },
        },
      },
    },

    // ── Sidebar drawer ────────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: warmBg.surface,
          borderRight: `1px solid ${alpha(accent[400], 0.15)}`,
          // Warm backdrop feel matching sidebar-panel
          backdropFilter: "blur(24px)",
        },
      },
    },

    // ── Top app bar ───────────────────────────────────────────────────────────
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: warmBg.surface,
          backgroundImage: "none",
          borderBottom: `1px solid ${alpha(accent[400], 0.2)}`,
          // Subtle warm glow shadow matching shadow-[0_20px_50px_rgba(0,0,0,0.5)]
          boxShadow: `0 2px 20px ${alpha("#000000", 0.5)}`,
        },
      },
    },

    // ── Cards / Paper ─────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: warmBg.paper,
          backgroundImage: "none",
          // Matches border border-white/10
          border: `1px solid ${hairline}`,
        },
        elevation1: {
          boxShadow: `0 4px 20px ${alpha("#000000", 0.55)}`,
        },
        elevation2: {
          boxShadow: `0 8px 32px ${alpha("#000000", 0.6)}`,
        },
      },
    },

    // ── Buttons — match the viewer's .btn-primary:
    // px-8 py-3 bg-panel-warm border-white/10 rounded-full text-[10px] font-bold
    // uppercase tracking-widest text-accent backdrop-blur hover:bg-accent/20
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "999px",         // rounded-full
          textTransform: "uppercase",
          letterSpacing: "0.1em",        // tracking-widest
          fontWeight: 700,
          fontSize: "10px",              // text-[10px]
        },
        contained: {
          backgroundColor: "rgba(20,15,15,0.5)",  // bg-panel-warm
          backdropFilter: "blur(20px)",
          border: `1px solid ${hairline}`,
          color: accent[400],
          padding: "12px 32px",          // px-8 py-3
          boxShadow: `0 10px 15px ${alpha("#000000", 0.2)}`,
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.2),
            boxShadow: `0 10px 15px ${alpha("#000000", 0.2)}`,
          },
        },
        // Matches btn-toggle-inactive: transparent bg, accent/30 border, accent text
        outlined: {
          borderColor: alpha(accent[400], 0.3),
          color: accent[400],
          "&:hover": {
            borderColor: accent[400],
            backgroundColor: alpha(accent[400], 0.2),
          },
        },
        text: {
          color: accent[400],
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.1),
          },
        },
      },
    },

    // ── Icon buttons — accent by default, like the viewer's btn-icon-accent ──
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "50%",
          color: accent[400],
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.15),
            color: accent[400],
          },
        },
        colorPrimary: {
          color: accent[400],
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.15),
          },
        },
      },
    },

    // ── Text fields / inputs ──────────────────────────────────────────────────
    // Matches the viewer's .popup-input/.sidebar-input:
    // bg-black/40 border-accent/30 rounded-md font-mono focus:border-accent
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          backgroundColor: "rgba(0,0,0,0.4)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(accent[400], 0.3),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(accent[400], 0.5),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: accent[400],
            borderWidth: "1px",
          },
          "& input": {
            fontFamily: fontMono,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          color: alpha(text.primary, 0.5),
          fontSize: "0.85rem",
          letterSpacing: "0.05em",
          "&.Mui-focused": {
            color: accent[400],
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
        },
      },
    },

    // ── Table rows (data grids / lists) ───────────────────────────────────────
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: `${alpha(accent[400], 0.06)} !important`,
          },
          "&.Mui-selected": {
            backgroundColor: `${alpha(accent[400], 0.1)} !important`,
          },
        },
        head: {
          backgroundColor: alpha(warmBg.surface, 0.8),
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          fontWeight: 600,
          letterSpacing: "0.1em",
          fontSize: "0.7rem",
          textTransform: "uppercase",
          color: alpha(text.primary, 0.55),
          borderBottom: `1px solid ${alpha(accent[400], 0.15)}`,
        },
        body: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          borderBottom: `1px solid ${alpha("#ffffff", 0.05)}`,
        },
      },
    },

    // ── Accordions (ProjectList) ───────────────────────────────────────────────
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: warmBg.paper,
          backgroundImage: "none",
          border: `1px solid ${alpha(accent[400], 0.12)}`,
          borderRadius: "12px !important",
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            borderColor: alpha(accent[400], 0.3),
            boxShadow: `0 4px 24px ${alpha("#000000", 0.5)}`,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.06),
          },
          "&.Mui-expanded": {
            borderRadius: "12px 12px 0 0",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            color: accent[400],
          },
        },
      },
    },

    // ── Sidebar list items ────────────────────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          mx: 0.5,
          "&.Mui-selected": {
            backgroundColor: alpha(accent[400], 0.15),
            borderLeft: `3px solid ${accent[400]}`,
            "&:hover": {
              backgroundColor: alpha(accent[400], 0.22),
            },
          },
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.1),
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: alpha(text.primary, 0.5),
          minWidth: 36,
          ".Mui-selected &": {
            color: accent[400],
          },
        },
      },
    },

    // ── Chips ─────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "999px",
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          fontSize: "0.7rem",
          letterSpacing: "0.05em",
        },
        colorPrimary: {
          backgroundColor: alpha(accent[400], 0.15),
          color: accent[300],
          border: `1px solid ${alpha(accent[400], 0.35)}`,
        },
      },
    },

    // ── Tooltips ──────────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: warmBg.surface,
          border: `1px solid ${alpha(accent[400], 0.3)}`,
          color: text.primary,
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          borderRadius: "8px",
          padding: "6px 12px",
        },
        arrow: {
          color: warmBg.surface,
        },
      },
    },

    // ── Divider ───────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: hairline,
        },
      },
    },

    // ── Select / Menu ─────────────────────────────────────────────────────────
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          "&:hover": {
            backgroundColor: alpha(accent[400], 0.1),
          },
          "&.Mui-selected": {
            backgroundColor: alpha(accent[400], 0.15),
            "&:hover": {
              backgroundColor: alpha(accent[400], 0.22),
            },
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: warmBg.surface,
          border: `1px solid ${alpha(accent[400], 0.2)}`,
          borderRadius: "12px",
          boxShadow: `0 8px 32px ${alpha("#000000", 0.6)}`,
        },
      },
    },

    // ── Dialog (Confirm modals etc.) ──────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: warmBg.paper,
          border: `1px solid ${alpha(accent[400], 0.3)}`,
          borderRadius: "16px",
          boxShadow: `0 20px 50px ${alpha("#000000", 0.7)}`,
          backgroundImage: "none",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          fontWeight: 300,
          letterSpacing: "0.04em",
          color: accent[300],
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
        },
      },
    },

    // ── Tabs (AereAppBar) ─────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: accent[400],
          height: "2px",
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Bodoni Moda", "Georgia", serif',
          fontWeight: 400,
          letterSpacing: "0.08em",
          fontSize: "0.8rem",
          textTransform: "uppercase",
          color: alpha(text.primary, 0.5),
          "&.Mui-selected": {
            color: accent[400],
          },
          "&:hover": {
            color: alpha(accent[400], 0.85),
            backgroundColor: alpha(accent[400], 0.07),
            borderRadius: "8px 8px 0 0",
          },
        },
      },
    },

    // ── Circular progress ─────────────────────────────────────────────────────
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: accent[400],
        },
      },
    },

    // ── Linear progress ───────────────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: alpha(accent[400], 0.15),
        },
        barColorPrimary: {
          backgroundColor: accent[400],
        },
      },
    },

    // ── Switches / checkboxes / radios ────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: accent[400],
            "+ .MuiSwitch-track": {
              backgroundColor: accent[600],
            },
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: alpha(accent[400], 0.4),
          "&.Mui-checked": {
            color: accent[400],
          },
        },
      },
    },

    // ── React-admin Login page ────────────────────────────────────────────────
    // Mirrors popup-content styling: bg-accent/30 border border-accent/70 rounded-2xl
    RaLogin: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          // Warm radial glow matching app login modal (bg-black/30 backdrop-blur)
          background: `radial-gradient(ellipse at center, ${alpha(accent[900], 0.4)} 0%, ${warmBg.default} 70%)`,
        },
        card: {
          backgroundColor: warmBg.paper,
          border: `1px solid ${alpha(accent[400], 0.4)}`,
          boxShadow: `0 20px 50px ${alpha("#000000", 0.6)}`,
          borderRadius: "16px",
          backgroundImage: "none",
        },
      },
    },
  },
});
